import React, { useMemo } from 'react';

// Primavera: pétalas e flores de cerejeira caindo em 3D (tumbling real com rotateX/Y/Z)
// + flores que "florescem" na tela e pólen subindo. CSS puro, sem dependência nova.
const PETAL_COLORS = ['#f9a8c8', '#ffc2d6', '#ffd9e6', '#f7b6d2', '#e9b8f0', '#ffd4b0'];
const FLOWER_COLORS = ['#f9a8c8', '#ffd9e6', '#e9b8f0', '#ffc2d6'];

const rand = (a, b) => a + Math.random() * (b - a);
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const Flor = ({ size, color }) => (
  <div className="ts-flor" style={{ width: size, height: size, '--c': color }}>
    {[0, 1, 2, 3, 4].map((i) => (
      <span key={i} className="ts-flor-petal" style={{ '--a': `${i * 72}deg` }} />
    ))}
    <span className="ts-flor-core" />
  </div>
);

export const ThemeSpring = ({ contained = false, count }) => {
  const scene = useMemo(() => {
    const nPetals = count || (contained ? 14 : 30);
    const items = [];
    for (let i = 0; i < nPetals; i++) {
      const w = contained ? rand(9, 16) : rand(12, 24);
      const far = Math.random() < 0.35;
      items.push({
        id: `p${i}`, kind: 'petal', left: rand(-3, 100), w,
        color: PETAL_COLORS[i % PETAL_COLORS.length],
        duration: rand(8, 18), delay: rand(0, 14),
        sway: rand(40, 150) * (Math.random() > 0.5 ? 1 : -1),
        opacity: far ? rand(0.4, 0.6) : rand(0.8, 1),
        blur: far ? rand(1, 2.4) : 0,
      });
    }
    const nFlowers = contained ? 2 : 5;
    for (let i = 0; i < nFlowers; i++) {
      items.push({
        id: `f${i}`, kind: 'flower', left: rand(2, 92),
        size: contained ? rand(20, 30) : rand(32, 58),
        color: pick(FLOWER_COLORS),
        duration: rand(13, 22), delay: i * 3 + rand(0, 6),
        sway: rand(50, 130) * (Math.random() > 0.5 ? 1 : -1),
        opacity: rand(0.85, 1), blur: 0,
      });
    }
    const nBlooms = contained ? 2 : 6;
    for (let i = 0; i < nBlooms; i++) {
      items.push({
        id: `b${i}`, kind: 'bloom', left: rand(6, 90), top: rand(14, 84),
        size: contained ? rand(22, 32) : rand(38, 66),
        color: pick(FLOWER_COLORS),
        duration: rand(8, 13), delay: i * 2.2 + rand(0, 5),
      });
    }
    const nPollen = contained ? 4 : 14;
    for (let i = 0; i < nPollen; i++) {
      items.push({
        id: `o${i}`, kind: 'pollen', left: rand(0, 100),
        size: contained ? rand(2, 4) : rand(3, 6),
        duration: rand(10, 20), delay: rand(0, 16),
        sway: rand(20, 90) * (Math.random() > 0.5 ? 1 : -1),
      });
    }
    return items;
  }, [contained, count]);

  const travel = contained ? '170px' : '116vh';

  return (
    <>
      <style>{`
        @keyframes ts-fall {
          0%   { transform: translate3d(0, calc(var(--travel) * -0.14), 0) rotateX(0deg) rotateY(0deg) rotateZ(0deg); opacity: 0; }
          7%   { opacity: var(--op); }
          25%  { transform: translate3d(var(--sw), calc(var(--travel) * 0.2), 70px) rotateX(130deg) rotateY(95deg) rotateZ(34deg); }
          50%  { transform: translate3d(calc(var(--sw) * -1), calc(var(--travel) * 0.46), -65px) rotateX(250deg) rotateY(205deg) rotateZ(-30deg); }
          75%  { transform: translate3d(calc(var(--sw) * 0.75), calc(var(--travel) * 0.73), 55px) rotateX(370deg) rotateY(320deg) rotateZ(40deg); }
          93%  { opacity: var(--op); }
          100% { transform: translate3d(0, var(--travel), 0) rotateX(520deg) rotateY(430deg) rotateZ(0deg); opacity: 0; }
        }
        @keyframes ts-bloom {
          0%, 100% { transform: scale(0) rotate(-35deg); opacity: 0; }
          10%  { transform: scale(1.18) rotate(9deg); opacity: 1; }
          20%  { transform: scale(1) rotate(0deg); opacity: 1; }
          70%  { transform: scale(1.02) rotate(3deg); opacity: 0.95; }
          88%  { transform: scale(1.14) rotate(-7deg); opacity: 0; }
        }
        @keyframes ts-halo {
          0%, 100% { transform: scale(0.4); opacity: 0; }
          22% { transform: scale(1.5); opacity: 0.55; }
          75% { transform: scale(2.1); opacity: 0; }
        }
        @keyframes ts-pollen {
          0%   { transform: translate3d(0, calc(var(--travel) * 0.98), 0) scale(0.5); opacity: 0; }
          18%  { opacity: 0.9; }
          70%  { opacity: 0.7; }
          100% { transform: translate3d(var(--sw), calc(var(--travel) * -0.06), 0) scale(1); opacity: 0; }
        }
        @keyframes ts-sun {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.08); }
        }
        .ts-petal {
          position: absolute;
          border-radius: 100% 0 100% 100% / 100% 0 62% 62%;
          background:
            linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.6) 50%, transparent 60%),
            radial-gradient(130% 130% at 22% 8%, #ffffff 0%, #ffe6f0 32%, var(--c) 72%, #ef94b8 100%);
          box-shadow: inset 0 -2px 5px rgba(186,86,128,0.28), 0 2px 5px rgba(180,80,120,0.18);
          transform-style: preserve-3d;
          backface-visibility: visible;
          will-change: transform, opacity;
        }
        .ts-flor { position: relative; transform-style: preserve-3d; }
        .ts-flor-petal {
          position: absolute; left: 50%; top: 50%;
          width: 48%; height: 64%; margin: -32% 0 0 -24%;
          border-radius: 62% 62% 44% 44% / 78% 78% 34% 34%;
          background: radial-gradient(115% 115% at 50% 100%, #fff3f8 0%, var(--c) 58%, #e988b0 100%);
          box-shadow: inset 0 -3px 6px rgba(186,86,128,0.22);
          transform: rotate(var(--a)) translateY(-33%);
        }
        .ts-flor-core {
          position: absolute; left: 50%; top: 50%; width: 27%; height: 27%;
          margin: -13.5% 0 0 -13.5%; border-radius: 50%;
          background: radial-gradient(circle at 38% 32%, #fff8cf 0%, #f8ce55 58%, #e0a400 100%);
          box-shadow: 0 0 7px rgba(248,206,85,0.85);
        }
      `}</style>

      {!contained && (
        <div data-testid="ts-sun" style={{
          position: 'absolute', top: '-6%', left: '-4%',
          width: 'clamp(160px, 26vw, 420px)', height: 'clamp(160px, 26vw, 420px)',
          borderRadius: '50%', filter: 'blur(6px)',
          background: 'radial-gradient(circle at 42% 42%, rgba(255,246,196,0.55) 0%, rgba(255,214,160,0.28) 38%, rgba(255,190,210,0.10) 62%, transparent 74%)',
          animation: 'ts-sun 9s ease-in-out infinite',
        }} />
      )}

      {scene.map((p) => {
        if (p.kind === 'bloom') {
          return (
            <div key={p.id} style={{ position: 'absolute', left: `${p.left}%`, top: `${p.top}%`,
              width: p.size, height: p.size }}>
              <div style={{ position: 'absolute', inset: '-40%', borderRadius: '50%',
                background: `radial-gradient(circle, ${p.color}66 0%, transparent 70%)`,
                animation: `ts-halo ${p.duration}s ease-out ${p.delay}s infinite` }} />
              <div style={{ position: 'absolute', inset: 0, opacity: 0, transformOrigin: '50% 50%',
                animation: `ts-bloom ${p.duration}s cubic-bezier(.34,1.56,.64,1) ${p.delay}s infinite` }}>
                <Flor size="100%" color={p.color} />
              </div>
            </div>
          );
        }
        if (p.kind === 'pollen') {
          return (
            <div key={p.id} style={{ position: 'absolute', left: `${p.left}%`, top: 0,
              width: p.size, height: p.size, borderRadius: '50%', opacity: 0,
              '--travel': travel, '--sw': `${p.sway}px`,
              background: 'radial-gradient(circle at 35% 35%, #fffdf0 0%, #ffe9a8 55%, rgba(255,220,140,0) 100%)',
              boxShadow: '0 0 8px rgba(255,235,170,0.9)',
              animation: `ts-pollen ${p.duration}s linear ${p.delay}s infinite` }} />
          );
        }
        const base = {
          position: 'absolute', left: `${p.left}%`, top: 0, opacity: 0,
          '--travel': travel, '--sw': `${p.sway}px`, '--op': p.opacity,
          filter: p.blur ? `blur(${p.blur}px)` : undefined,
          animation: `ts-fall ${p.duration}s linear ${p.delay}s infinite`,
        };
        if (p.kind === 'flower') {
          return (
            <div key={p.id} style={{ ...base, width: p.size, height: p.size }}>
              <Flor size="100%" color={p.color} />
            </div>
          );
        }
        return (
          <div key={p.id} className="ts-petal"
               style={{ ...base, width: p.w, height: p.w * 0.86, '--c': p.color }} />
        );
      })}
    </>
  );
};

export default ThemeSpring;
